import { pickDefaultUnitId } from '#/modules/imsMgmt/helper/materialUnit/pickDefaultUnitId';
import { resolveUnitId } from '#/modules/imsMgmt/helper/materialUnit/resolveUnitId';
import {
  PRICE_CALC_MODE,
} from '#/modules/imsMgmt/receipts/stockReceipt/helper/unitPriceFromTotal';
import {
  getStockReceiptSourceUiProfile,
} from '#/modules/imsMgmt/receipts/stockReceipt/helper/stockReceiptSource';
import { pickRoundingMode } from '#/utils/roundingMode';

/**
 * 表單值 → IMS create／update body（一般 `/ims/stock-receipt`）。
 * MANUAL：送 supplierId、warehouseId、currencyId、roundingMode（合法 enum；勿空值偷送 HALF_UP）、
 * receiptDate、remark、issueReason、關稅／運費、lines（packages 僅 location／qty／remark）。
 * PURCHASE：可送 warehouseId／lines／關稅／運費；**不送** supplier／currency／rounding。
 * WORK_ORDER：可送 warehouseId／lines；**不送** 關稅／運費與商業 header。
 * **一律不送**：expectedArrivalDate、派生金額、convertRate、packages[].unitId、
 * sourceType／sourceId。
 */

function isBlankHeaderCharge(value) {
  return value === '' || value == null;
}

/** 空白合法。有值時須為非負、最多兩位小數。 */
export function isValidStockReceiptHeaderCharge(value) {
  if (isBlankHeaderCharge(value)) {
    return true;
  }
  const text = String(value).trim();
  if (!/^\d+(\.\d{1,2})?$/.test(text)) {
    return false;
  }
  const amount = Number(text);
  return Number.isFinite(amount) && amount >= 0;
}

/** 空白或非法 → null（建立當 0；草稿 PATCH 的 null 重設為 0）。 */
export function parseStockReceiptHeaderCharge(value) {
  if (!isValidStockReceiptHeaderCharge(value) || isBlankHeaderCharge(value)) {
    return null;
  }
  return Number(String(value).trim());
}

export function headerChargePreviewAmount(value) {
  return parseStockReceiptHeaderCharge(value) ?? 0;
}

export function buildStockReceiptWritePayload(values, { updatedAt = null } = {}) {
  const sourceType = values.sourceType || 'MANUAL';
  const payload = {
    receiptDate: values.receiptDate || null,
    warehouseId: values.warehouseId || null,
    remark: values.remark || null,
    issueReason: values.issueReason || null,
    lines: (values.lines ?? []).map((line) => ({
      mtrId: line.mtrId,
      unitId: line.unitId,
      unitPrice: line.unitPrice === '' || line.unitPrice == null
        ? null
        : Number(line.unitPrice),
      remark: line.remark || null,
      packages: (line.packages ?? []).map((pkg) => ({
        locationId: pkg.locationId === '' || pkg.locationId == null
          ? null
          : pkg.locationId,
        qty: pkg.qty === '' || pkg.qty == null ? null : Number(pkg.qty),
        remark: pkg.remark || null,
      })),
    })),
  };

  if (sourceType === 'MANUAL') {
    payload.supplierId = values.supplierId || null;
    payload.currencyId = values.currencyId || null;
    payload.roundingMode = pickRoundingMode(values.roundingMode);
  }

  if (sourceType !== 'WORK_ORDER') {
    payload.customsDutyAmount = parseStockReceiptHeaderCharge(values.customsDutyAmount);
    payload.freightAmount = parseStockReceiptHeaderCharge(values.freightAmount);
  }

  if (updatedAt) {
    payload.updatedAt = updatedAt;
  }

  return payload;
}

/**
 * detail → 表單 defaultValues。
 * mtrNo／mtrName 僅顯示用；物料 id 取 `mtr.id`（相容舊 `material`）。
 * 幣別：頂層 `currencyId`，否則 `amountSummary.currencyId`（不讀 `currency` 物件）。
 * 進位：`amountSummary.roundingMode`（相容 `amount.roundingMode`）。**不**把 amountSummary 回寫。
 * packages locationId ← locationId ?? location?.id。
 */
export function mapStockReceiptDetailToFormValues(detail) {
  const amountSummary = detail?.amountSummary ?? null;
  const sourceType = detail?.sourceType ?? 'MANUAL';
  const sourceProfile = getStockReceiptSourceUiProfile(sourceType);
  const clearCommercial = sourceProfile.clearCommercialHeaderOnHydrate;

  return {
    receiptDate: detail?.receiptDate ?? '',
    expectedArrivalDate: '',
    warehouseId: detail?.warehouse?.id ?? detail?.warehouseId ?? '',
    supplierId: clearCommercial
      ? ''
      : (detail?.supplier?.id ?? detail?.supplierId ?? ''),
    currencyId: clearCommercial
      ? ''
      : (detail?.currencyId
        ?? amountSummary?.currencyId
        ?? ''),
    roundingMode: clearCommercial
      ? ''
      : (pickRoundingMode(
        amountSummary?.roundingMode
          ?? detail?.roundingMode
          ?? detail?.amount?.roundingMode,
      ) ?? ''),
    remark: detail?.remark ?? '',
    issueReason: detail?.issueReason ?? '',
    customsDutyAmount: clearCommercial
      ? ''
      : (amountSummary?.customsDutyAmount ?? ''),
    freightAmount: clearCommercial
      ? ''
      : (amountSummary?.freightAmount ?? ''),
    sourceType,
    sourceId: detail?.sourceId ?? '',
    lines: (detail?.lines ?? []).map((line) => {
      const mtr = line?.mtr ?? line?.material ?? null;
      const unitOptions = mtr?.unitOptions
        ?? line?.unitOptions
        ?? [];
      const lineUnitId = resolveUnitId(line?.unit)
        || resolveUnitId(line)
        || pickDefaultUnitId(unitOptions);
      const rawUnitPrice = line.unitPrice;

      return {
        mtrId: mtr?.id ?? line.mtrId ?? '',
        mtrNo: mtr?.mtrNo ?? line.mtrNo ?? '',
        mtrName: mtr?.name ?? line.mtrName ?? '',
        unitId: lineUnitId,
        unitPrice: rawUnitPrice ?? '',
        remark: line.remark ?? '',
        unitOptions,
        priceCalcMode: PRICE_CALC_MODE.FROM_UNIT_PRICE,
        priceTotal: '',
        priceInput: rawUnitPrice ?? '',
        packages: (line.packages ?? []).map((pkg) => ({
          locationId: pkg.locationId ?? pkg.location?.id ?? '',
          qty: pkg.qty ?? pkg.quantity ?? '',
          remark: pkg.remark ?? '',
        })),
      };
    }),
  };
}

/**
 * @param {object} [overrides]
 */
export function createEmptyPackage(overrides = {}) {
  return {
    locationId: '',
    qty: '',
    remark: '',
    ...overrides,
  };
}

export function createEmptyLine(overrides = {}) {
  const {
    packages,
    ...rest
  } = overrides;

  return {
    mtrId: '',
    mtrNo: '',
    mtrName: '',
    unitId: '',
    unitPrice: '',
    remark: '',
    unitOptions: [],
    priceCalcMode: PRICE_CALC_MODE.FROM_UNIT_PRICE,
    priceTotal: '',
    priceInput: '',
    packages: packages ?? [createEmptyPackage()],
    ...rest,
  };
}

/** MtrPickerDialog 選列 → 入庫明細列（同 mtrId 不重複由呼叫端過濾）。 */
export function mapMtrPickerRowToReceiptLine(row) {
  const mtrId = row?.optionMtrId ?? row?.id ?? '';
  if (!mtrId) {
    return null;
  }

  const mtrNo = row.mtrNo ?? '';
  const mtrName = row.name
    || mtrNo
    || String(mtrId).slice(-8);
  const unitPrice = row.baseUnitPrice ?? row.unitPrice ?? '';
  const hasPrice = unitPrice !== '' && unitPrice != null;
  const receiptDefaultQty = row.receiptDefaultQty;
  const hasDefaultQty = receiptDefaultQty !== ''
    && receiptDefaultQty != null
    && Number.isFinite(Number(receiptDefaultQty));

  return createEmptyLine({
    mtrId,
    mtrNo,
    mtrName,
    ...(hasPrice ? {
      unitPrice,
      priceCalcMode: PRICE_CALC_MODE.FROM_UNIT_PRICE,
      priceInput: unitPrice,
    } : {}),
    ...(hasDefaultQty ? {
      packages: [createEmptyPackage({ qty: Number(receiptDefaultQty) })],
    } : {}),
  });
}

/**
 * 選料確認：只回傳明細尚無的新列（已存在 mtrId 則 skip，不覆寫單價）。
 */
export function mapNewMtrPickerRowsToReceiptLines(existingMtrIds, selectedRows) {
  const existingIds = new Set(existingMtrIds ?? []);
  const added = [];

  (selectedRows ?? []).forEach((row) => {
    const mtrId = row?.optionMtrId ?? row?.id;
    if (!mtrId || existingIds.has(mtrId)) {
      return;
    }
    const line = mapMtrPickerRowToReceiptLine(row);
    if (line) {
      added.push(line);
      existingIds.add(mtrId);
    }
  });

  return added;
}

/** 包裝列數量合計（line 單位）。 */
export function sumLineQty(line) {
  return (line?.packages ?? []).reduce(
    (sum, pkg) => sum + (Number(pkg?.qty) || 0),
    0,
  );
}

/** 金額＝Σ(packages.qty)×unitPrice（unitPrice＝line 單位價；僅表單預覽）。 */
export function calcLineAmount(line) {
  return sumLineQty(line) * (Number(line?.unitPrice) || 0);
}

export function calcDocumentTotalAmount(lines = []) {
  return lines.reduce((sum, line) => sum + calcLineAmount(line), 0);
}

export function formatMoneyLabel(amount) {
  const n = Number(amount) || 0;
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}
